import React, { useState, useEffect, useMemo } from "react";
import { X, MapPin, Phone, Package, Truck, Edit2, Trash2 } from "lucide-react";
import { Button } from "@material-tailwind/react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { updateProfile } from "../../Redux/userSlice";
import BarLoader from "../common/BarLoader";
import { computeDeliveryFee, formatNaira } from "../../utils/pricing";
import api from "../../utils/api";

export default function ConfirmOrderScreen({
  isOpen,
  onClose,
  onContinue,
  orderData,
  onEdit,
  onServerUpdated,
  darkMode,

}) {
  // const { socket, isConnected, uploadFile } = useSocket();
  const currentUser = useSelector(s => s.auth?.user || s.auth?.userData || s.auth);
  const dispatch = useDispatch();
  const [isConnecting, setIsConnecting] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(null);
  const [locationError, setLocationError] = useState(null);

  const {
    serviceType,
    marketLocation,
    pickupLocation,
    deliveryLocation,
    fleetType,
    specialInstructions,
    pickupPhone,
    dropoffPhone,
    deliveryPhone,
    marketItems,
    budget,
    canAdjustSlightly, // eslint-disable-line no-unused-vars
    budgetFlexibility,
    estimatedPrice, // eslint-disable-line no-unused-vars
    marketCoordinates, // eslint-disable-line no-unused-vars
    deliveryCoordinates,  // eslint-disable-line no-unused-vars
  } = orderData || {};

  const midOrPickupCoords = serviceType === "run-errand"
    ? orderData?.marketCoordinates
    : orderData?.pickupCoordinates;

  useEffect(() => {
    let cancelled = false;

    computeDeliveryFee(serviceType, midOrPickupCoords, orderData?.deliveryCoordinates, fleetType)
      .then(({ deliveryFee }) => {
        if (!cancelled) setDeliveryFee(deliveryFee);
      })
      .catch((err) => {
        console.error('[ConfirmOrder] Failed to compute delivery fee:', err);
        if (!cancelled) setDeliveryFee(null);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType, fleetType, midOrPickupCoords?.lat, midOrPickupCoords?.lng, orderData?.deliveryCoordinates?.lat, orderData?.deliveryCoordinates?.lng]);


  const handleEdit = (field) => {
    onEdit(field);
    onClose();
  };

  const getFreshLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported on this device.'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          let message;
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = 'Location access was denied. Please enable location permissions and try again.';
              break;
            case error.POSITION_UNAVAILABLE:
              message = 'Your current location could not be determined. Please check your device settings and try again.';
              break;
            case error.TIMEOUT:
              message = 'Getting your location took too long. Please try again.';
              break;
            default:
              message = 'Unable to get your current location. Please try again.';
          }
          reject(new Error(message));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  const reverseGeocode = (lat, lng) => {
    return new Promise((resolve) => {
      if (!window.google) {
        resolve(`Location (${lat.toFixed(6)}, ${lng.toFixed(6)})`);
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          resolve(results[0].formatted_address);
        } else {
          resolve(`Location (${lat.toFixed(6)}, ${lng.toFixed(6)})`);
        }
      });
    });
  };

  const handleContinueClick = async () => {
    if (isConnecting) return;
    
    try {
      setIsConnecting(true);
      setLocationError(null);
      const userId = currentUser?._id;

      console.log('[ConfirmOrder] orderData.specialInstructions:',
        JSON.stringify(orderData?.specialInstructions, null, 2));
      console.log('[ConfirmOrder] media array:',
        orderData?.specialInstructions?.media?.map(m => ({
          name: m.fileName || m.name,
          hasFile: !!m.file,
          fileUrl: m.fileUrl
        })));

      if (!userId) {
        alert('User not found. Please login again.');
        setIsConnecting(false);
        return;
      }

      let freshCurrentUserLocation = orderData?.currentUserLocation;
      let freshCurrentUserCoordinates = orderData?.currentUserCoordinates;
      try {
        const coords = await getFreshLocation();
        const address = await reverseGeocode(coords.latitude, coords.longitude);
        freshCurrentUserLocation = address;
        freshCurrentUserCoordinates = { lat: coords.latitude, lng: coords.longitude };
      } catch (locError) {
        console.warn('[ConfirmOrder] Could not refresh location, falling back to stored value:', locError.message);
        if (!freshCurrentUserLocation) {
          setLocationError(locError.message + ' Tap Continue to try again.');
          return;
        }
        setLocationError('Could not refresh your location — using your last known location instead.');
      }

      // ── Upload special instruction media first ──────────────────────────────
      let uploadedMedia = [];
      if (
        orderData?.specialInstructions &&
        typeof orderData.specialInstructions === 'object' &&
        orderData.specialInstructions.media?.length > 0
      ) {

        uploadedMedia = await Promise.all(
          orderData.specialInstructions.media.map(async (media) => {
            if (media.fileUrl) return { // already uploaded
              fileName: media.fileName || media.name,
              fileType: media.fileType || media.type,
              fileSize: media.fileSize || media.size,
              fileUrl: media.fileUrl,
            };

            const formData = new FormData();

            if (media.file) {
              formData.append('file', media.file); // use File object directly
            } else if (media.preview) {
              const blob = await fetch(media.preview).then(r => r.blob());
              const file = new File([blob], media.fileName || media.name, {
                type: media.fileType || media.type
              });
              formData.append('file', file);
            } else {
              return null;
            }

            const res = await api.post('/upload/special-instructions', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });

            return {
              fileName: media.fileName || media.name,
              fileType: media.fileType || media.type,
              fileSize: media.fileSize || media.size,
              fileUrl: res.data.secure_url,
            };
          })
        );

        uploadedMedia = uploadedMedia.filter(Boolean);
      }

      // ── Build resolved special instructions ────────────────────────────────
      const resolvedSpecialInstructions = orderData?.specialInstructions
        ? {
          text: orderData.specialInstructions.text || null,
          media: uploadedMedia,
        }
        : null;

      await dispatch(updateProfile({
        currentRequest: {
          serviceType,
          fleetType,
          userId,
          currentUserLocation: freshCurrentUserLocation,
          currentUserCoordinates: freshCurrentUserCoordinates,

          specialInstructions: resolvedSpecialInstructions,
          timestamp: new Date().toISOString(),
          dropoffPhone: orderData?.dropoffPhone || orderData?.deliveryPhone,
          deliveryLocation: orderData?.deliveryLocation,
          status: 'awaiting_runner_connection',

          ...(serviceType === 'run-errand' ? {
            marketLocation: orderData?.marketLocation,
            marketItems: orderData?.marketItems,
            budget: orderData?.budget,
            canAdjustSlightly: orderData?.canAdjustSlightly ?? false,
            budgetFlexibility: orderData?.budgetFlexibility || 'stay within budget',
            marketCoordinates: orderData?.marketCoordinates,
            deliveryCoordinates: orderData?.deliveryCoordinates,
          } : {
            pickupLocation: orderData?.pickupLocation,
            pickupItems: orderData?.pickupItems,
            pickupPhone: orderData?.pickupPhone,
            pickupCoordinates: orderData?.pickupCoordinates,
            deliveryCoordinates: orderData?.deliveryCoordinates,
          }),
        }
      })).unwrap();

      onServerUpdated();
      onContinue();
    } catch (error) {
      console.error('Failed:', error);
      alert(error?.message || 'Failed. Please try again.');
    } finally {
      setIsConnecting(false);
    }
  };

  const locationToDisplay = serviceType === "run-errand" ? marketLocation : pickupLocation;

  const mediaPreviews = useMemo(() => {
    if (!specialInstructions?.media) return {};
    return specialInstructions.media.reduce((acc, media, idx) => {
      if (media.fileUrl) {
        acc[idx] = media.fileUrl;
      } else if (media.file instanceof File) {
        acc[idx] = URL.createObjectURL(media.file);
      } else if (media.preview) {
        acc[idx] = media.preview;
      } else {
        acc[idx] = null;
      }
      return acc;
    }, {});
  }, [specialInstructions?.media]);

  // cleanups
  useEffect(() => {
    return () => {
      Object.values(mediaPreviews).forEach(url => {
        if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
      });
    };
  }, [mediaPreviews]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className={`w-full scrollbar-hide scroll-smooth sm:max-w-lg sm:mx-4 sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto ${darkMode ? "bg-black-100 text-white" : "bg-white text-black-200"
          }`}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 bg-inherit">
          <h2 className="text-xl font-bold">Confirm Your Order</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">

          {/* Delivery Fee */}
          <div className="p-4 bg-primary/10 rounded-lg">
            <p className="text-xs font-medium opacity-60 mb-1">Delivery Fee</p>
            <p className="text-lg font-bold text-primary">
              {deliveryFee != null ? formatNaira(deliveryFee) : "Calculating..."}
            </p>
          </div>

          {/* Service Type */}
          <div className="flex items-start justify-between p-3 bg-gray-200 dark:bg-black-100 border-b border-t dark:border-gray-800/40">
            <div className="flex gap-1 justify-center items-center">
              <p className="text-sm font-medium opacity-70">Service Type: </p> <span className="text-md font-semibold capitalize">{serviceType?.replace("-", " ")}</span>
            </div>
          </div>

          {/* Pickup/Market Location */}
          <div className="flex items-start justify-between p-3 bg-gray-200 dark:bg-black-100 border-b dark:border-gray-800/40 gap-2">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <MapPin className="h-5 w-5 mt-0.5 text-green-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium opacity-70">
                  {serviceType === "run-errand" ? "Market Location" : "Pickup Location"}
                </p>
                <p className="font-semibold truncate">{locationToDisplay}</p>
              </div>
            </div>
            <button
              onClick={() => handleEdit(serviceType === "run-errand" ? "market-location" : "pickup-location")}
              className="p-2 border-gray-800 border rounded-lg transition-colors ml-2"
            >
              <Edit2 className="h-4 w-4 text-primary" />
            </button>
          </div>

          {/* Pickup Phone (only for pick-up service) */}
          {serviceType === "pick-up" && pickupPhone && (
            <div className="flex items-start justify-between p-3 bg-gray-200 dark:bg-black-100 border-b dark:border-gray-800/40">
              <div className="flex items-start gap-3 flex-1">
                <Phone className="h-5 w-5 mt-0.5 text-secondary" />
                <div>
                  <p className="text-sm font-medium opacity-70">Pickup Contact</p>
                  <p className="font-semibold">{pickupPhone}</p>
                </div>
              </div>
              <button
                onClick={() => handleEdit("pickup-phone")}
                className="p-2 border-gray-800 border rounded-lg transition-colors ml-2"
              >
                <Edit2 className="h-4 w-4 text-primary" />
              </button>
            </div>
          )}

          {/* Pickup Items (only for pick-up service) */}
          {serviceType === "pick-up" && orderData?.pickupItems && (
            <div className="flex items-start justify-between p-3 bg-gray-200 dark:bg-black-100 border-b dark:border-gray-800/40">
              <div className="flex items-start gap-3 flex-1">
                <Package className="h-5 w-5 mt-0.5 text-orange-500" />
                <div>
                  <p className="text-sm font-medium opacity-70">Item(s) to Pick Up</p>
                  <p className="font-semibold">{orderData.pickupItems}</p>
                </div>
              </div>
              <button
                onClick={() => handleEdit("pickup-items")}
                className="p-2 border-gray-800 border rounded-lg transition-colors ml-2"
              >
                <Edit2 className="h-4 w-4 text-primary" />
              </button>
            </div>
          )}

          {/* Market Items (only for run-errand) */}
          {serviceType === "run-errand" && marketItems && (
            <div className="flex items-start justify-between p-3 bg-gray-200 dark:bg-black-100 border-b dark:border-gray-800/40">
              <div className="flex items-start gap-3 flex-1">
                <Package className="h-5 w-5 mt-0.5 text-orange-500" />
                <div>
                  <p className="text-sm font-medium opacity-70">Items to Buy</p>
                  <p className="font-semibold">{marketItems}</p>
                </div>
              </div>
              <button
                onClick={() => handleEdit("market-items")}
                className="p-2 border-gray-800 border rounded-lg transition-colors ml-2"
              >
                <Edit2 className="h-4 w-4 text-primary" />
              </button>
            </div>
          )}

          {/* Budget (only for run-errand) */}
          {serviceType === "run-errand" && budget && (
            <div className="flex items-start justify-between p-3 bg-gray-200 dark:bg-black-100 border-b dark:border-gray-800/40">
              <div className="flex items-start gap-3 flex-1">
                <p className="h-5 w-5 mt-0.5 text-green-600">₦</p>
                <div>
                  <p className="text-sm font-medium opacity-70">Budget</p>
                  <p className="font-semibold">₦{budget.toLocaleString()}</p>
                  <p className="text-xs opacity-60 mt-1">
                    {budgetFlexibility === "stay within budget" ? "Strict budget" : "Flexible budget"}
                  </p>
                  {canAdjustSlightly && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
                      <p className="text-xs text-amber-400 font-medium">
                        Budget can be adjusted slightly if needed
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => handleEdit("market-budget")}
                className="p-2 border-gray-800 border rounded-lg transition-colors ml-2"
              >
                <Edit2 className="h-4 w-4 text-primary" />
              </button>
            </div>
          )}

          {/* Delivery Location */}
          <div className="flex items-start justify-between p-3 bg-gray-200 dark:bg-black-100 border-b dark:border-gray-800/40 gap-2">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <MapPin className="h-5 w-5 mt-0.5 text-red-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium opacity-70">Delivery Location</p>
                <p className="font-semibold truncate">{deliveryLocation}</p>
              </div>
            </div>
            <button
              onClick={() => handleEdit("delivery-location")}
              className="p-2 border-gray-800 border rounded-lg transition-colors ml-2"
            >
              <Edit2 className="h-4 w-4 text-primary" />
            </button>
          </div>

          {/* Dropoff Phone (only for pick-up service) */}
          {(dropoffPhone || deliveryPhone) && (
            <div className="flex items-start justify-between p-3 bg-gray-200 dark:bg-black-100 border-b dark:border-gray-800/40">
              <div className="flex items-start gap-3 flex-1">
                <Phone className="h-5 w-5 mt-0.5 text-purple-500" />
                <div>
                  <p className="text-sm font-medium opacity-70">
                    {serviceType === "run-errand" ? "Delivery Contact" : "Dropoff Contact"}
                  </p>
                  <p className="font-semibold">
                    <p className="font-semibold">{dropoffPhone || deliveryPhone}</p>
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleEdit(serviceType === "run-errand" ? "delivery-phone" : "dropoff-phone")}
                className="p-2 border-gray-800 border rounded-lg transition-colors ml-2"
              >
                <Edit2 className="h-4 w-4 text-primary" />
              </button>
            </div>
          )}

          {/* Fleet Type */}
          <div className="flex items-start justify-between p-3 bg-gray-200 dark:bg-black-100 border-b dark:border-gray-800/40">
            <div className="flex items-start gap-3 flex-1">
              <Truck className="h-5 w-5 mt-0.5 text-indigo-500" />
              <div>
                <p className="text-sm font-medium opacity-70">Vehicle Type</p>
                <p className="font-semibold capitalize">{fleetType}</p>
              </div>
            </div>
            <button
              onClick={() => handleEdit("fleet-type")}
              className="p-2 border-gray-800 border rounded-lg transition-colors ml-2"
            >
              <Edit2 className="h-4 w-4 text-primary" />
            </button>
          </div>

          {/* Special Instructions */}
          {specialInstructions && (
            (typeof specialInstructions === 'string' && specialInstructions.trim()) ||
            (typeof specialInstructions === 'object' && (specialInstructions.text?.trim() || specialInstructions.media?.length > 0))
          ) && (
              <div className="flex flex-col p-3 rounded-lg border border-gray-300">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="flex-1">
                      <p className="text-sm font-medium">Special Instructions</p>

                      {typeof specialInstructions === 'string' && (
                        <p className="font-semibold whitespace-pre-wrap mt-1 text-sm">{specialInstructions}</p>
                      )}

                      {typeof specialInstructions === 'object' && (
                        <>
                          {specialInstructions.text && (
                            <p className="font-semibold whitespace-pre-wrap mt-1">{specialInstructions.text}</p>
                          )}

                          {specialInstructions.media && specialInstructions.media.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {specialInstructions.media.map((media, idx) => {
                                const previewUrl = mediaPreviews[idx];

                                return (
                                  <div key={idx} className="relative flex-shrink-0">
                                    {media.type?.startsWith('image/') && previewUrl ? (
                                      <img
                                        src={previewUrl}
                                        alt="Attachment"
                                        className="w-20 h-20 object-cover rounded-lg border-2 border-gray-300 dark:border-gray-600"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                    ) : media.type?.startsWith('audio/') ? (
                                      <div className="w-full min-w-[250px] bg-primary rounded-lg p-2">
                                        <audio controls className="w-full h-10" style={{ maxWidth: '100%' }}>
                                          <source src={previewUrl} type="audio/webm" />
                                          <source src={previewUrl} type="audio/mpeg" />
                                          <source src={previewUrl} type="audio/mp3" />
                                          Your browser does not support audio playback.
                                        </audio>
                                      </div>
                                    ) : (
                                      <div className="w-20 h-20 bg-gray-200 dark:bg-gray-700 rounded-lg flex flex-col items-center justify-center">
                                        <span className="text-2xl mb-1">📎</span>
                                        <span className="text-xs truncate max-w-full px-1">
                                          {media.name?.split('.').pop()}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => handleEdit("special-instructions")}
                      className="p-2 border-gray-800 border rounded-lg transition-colors ml-2 self-start"
                    >
                      <Edit2 className="h-4 w-4 text-primary" />
                    </button>
                    <button
                      onClick={() => handleEdit("special-instructions")}
                      className="p-2 border-gray-800 border rounded-lg transition-colors ml-2 self-start"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 p-4 bg-inherit">
          {locationError && (
            <p className="text-xs text-amber-500 mb-2 text-center">{locationError}</p>
          )}
          <Button
            onClick={handleContinueClick}
            disabled={isConnecting}
            className={`w-full py-3 text-lg font-semibold flex items-center justify-center gap-2 transition-colors ${isConnecting ? 'cursor-not-allowed bg-gray-400 text-white' : 'bg-primary text-white'}`}
          >
            {isConnecting ? (
              <>
                <BarLoader size="small" />
                <span>Connecting...</span>
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}